import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PackageForm } from './PackageForm';
import { PlanSelector } from './PlanSelector';
import { CategorySelector } from './CategorySelector';
import { ChannelSelector } from './ChannelSelector';
import { PackageFormData, Package } from '@/features/packages/types';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Plan {
  id: number;
  name: string;
  monthly_price?: number | null;
}

interface Category {
  id: number;
  name: string;
}

interface Channel {
  id: number;
  name: string;
}

interface PackageComposerProps {
  initialData?: Package;
  availablePlans: Plan[];
  availableCategories: Category[];
  availableChannels: Channel[];
  isLoading?: boolean;
  isSubmitting?: boolean;
  onSubmit: (
    packageData: PackageFormData,
    planIds: number[],
    categoryIds: number[],
    channelIds: number[]
  ) => Promise<void>;
  onCancel?: () => void;
}

export const PackageComposer: React.FC<PackageComposerProps> = ({
  initialData,
  availablePlans,
  availableCategories,
  availableChannels,
  isLoading = false,
  isSubmitting = false,
  onSubmit,
  onCancel,
}) => {
  const [packageData, setPackageData] = useState<PackageFormData | null>(null);
  const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>(
    initialData?.plans?.map((p) => p.id) || []
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
    initialData?.categories?.map((c) => c.id) || []
  );
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>(
    initialData?.channels?.map((ch) => ch.id) || []
  );

  const handleFormSubmit = async (data: PackageFormData) => {
    setPackageData(data);
  };

  const handleComposerSubmit = async () => {
    if (!packageData) return;
    await onSubmit(packageData, selectedPlanIds, selectedCategoryIds, selectedChannelIds);
  };

  const totalChannels = selectedChannelIds.length;
  const plansWithChannels =
    selectedPlanIds.length > 0
      ? availablePlans
          .filter((p) => selectedPlanIds.includes(p.id))
          .reduce((count) => count + (availableChannels.length || 0), 0)
      : 0;
  const categoriesWithChannels =
    selectedCategoryIds.length > 0 ? availableChannels.length : 0;

  const estimatedChannelCount =
    totalChannels +
    (selectedPlanIds.length > 0 ? plansWithChannels : 0) +
    (selectedCategoryIds.length > 0 ? categoriesWithChannels : 0);

  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Main Form */}
      <div className="col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>Package Details</CardTitle>
            <CardDescription>Configure basic package information</CardDescription>
          </CardHeader>
          <CardContent>
            <PackageForm
              initialData={initialData}
              isLoading={isLoading}
              onSubmit={handleFormSubmit}
              onCancel={onCancel}
            />
          </CardContent>
        </Card>
      </div>

      {/* Composition Panel */}
      <div className="col-span-1">
        <Card className="sticky top-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Package Composition</CardTitle>
            <CardDescription>Add plans, categories & channels</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs defaultValue="plans" className="w-full">
              <TabsList className="grid w-full grid-cols-3 text-xs">
                <TabsTrigger value="plans" className="text-xs">
                  Plans
                  {selectedPlanIds.length > 0 && (
                    <Badge className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                      {selectedPlanIds.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="categories" className="text-xs">
                  Categories
                  {selectedCategoryIds.length > 0 && (
                    <Badge className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                      {selectedCategoryIds.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="channels" className="text-xs">
                  Channels
                  {selectedChannelIds.length > 0 && (
                    <Badge className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                      {selectedChannelIds.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Plans Tab */}
              <TabsContent value="plans" className="mt-4 max-h-80 overflow-y-auto">
                <PlanSelector
                  availablePlans={availablePlans}
                  selectedPlanIds={selectedPlanIds}
                  onSelectionChange={setSelectedPlanIds}
                  isLoading={isLoading}
                  maxHeight="max-h-64"
                />
              </TabsContent>

              {/* Categories Tab */}
              <TabsContent value="categories" className="mt-4 max-h-80 overflow-y-auto">
                <CategorySelector
                  availableCategories={availableCategories}
                  selectedCategoryIds={selectedCategoryIds}
                  onSelectionChange={setSelectedCategoryIds}
                  isLoading={isLoading}
                  maxHeight="max-h-64"
                />
              </TabsContent>

              {/* Channels Tab */}
              <TabsContent value="channels" className="mt-4 max-h-80 overflow-y-auto">
                <ChannelSelector
                  availableChannels={availableChannels}
                  selectedChannelIds={selectedChannelIds}
                  onSelectionChange={setSelectedChannelIds}
                  isLoading={isLoading}
                  maxHeight="max-h-64"
                />
              </TabsContent>
            </Tabs>

            {/* Channel Estimate */}
            <div className="border-t pt-4">
              <p className="mb-2 text-xs font-semibold text-gray-700">Estimated Channels</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Direct channels:</span>
                  <Badge variant="outline">{totalChannels}</Badge>
                </div>
                {selectedPlanIds.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">From {selectedPlanIds.length} plan(s):</span>
                    <Badge variant="outline">~{selectedPlanIds.length}</Badge>
                  </div>
                )}
                {selectedCategoryIds.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">From {selectedCategoryIds.length} categor{selectedCategoryIds.length !== 1 ? 'ies' : 'y'}:</span>
                    <Badge variant="outline">~{selectedCategoryIds.length}</Badge>
                  </div>
                )}
                <div className="border-t pt-2">
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-800">Total estimate:</span>
                    <Badge className="bg-blue-600">~{estimatedChannelCount}</Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="border-t pt-4">
              <Button
                onClick={handleComposerSubmit}
                disabled={isSubmitting || !packageData}
                className="w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                {initialData ? 'Update' : 'Create'} Package
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PackageComposer;
